import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Reusable CSV import dialog.
 * Props:
 *  - open / onOpenChange
 *  - title, description (string)
 *  - templateUrl  (e.g. "/timesheets/import/template")  — relative to /api
 *  - importUrl    (e.g. "/timesheets/import")
 *  - templateFilename (e.g. "timesheet_template.csv")
 *  - onDone(result)  — called after a successful POST so the parent can refresh
 *  - testIdPrefix  (e.g. "ts-import")
 */
export default function CsvImportDialog({
  open, onOpenChange, title, description,
  templateUrl, importUrl, templateFilename = "template.csv",
  onDone, testIdPrefix = "csv-import",
}) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setResult(null); if (fileRef.current) fileRef.current.value = ""; };

  const handleClose = (v) => { if (!v) reset(); onOpenChange?.(v); };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(templateUrl, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = templateFilename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please choose a .csv file."); return;
    }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await api.post(importUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(res.data);
      const { created = 0, skipped = 0 } = res.data || {};
      if (created > 0 && skipped === 0) toast.success(`Imported ${created} row${created === 1 ? "" : "s"}.`);
      else if (created > 0) toast.success(`Imported ${created}, skipped ${skipped}.`);
      else toast.error(`Nothing imported. ${skipped} row${skipped === 1 ? "" : "s"} skipped.`);
      onDone?.(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-3xl max-w-md max-h-[90vh] overflow-y-auto" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" /> {title}
          </DialogTitle>
          {description && <DialogDescription className="text-xs text-muted-foreground">{description}</DialogDescription>}
        </DialogHeader>

        {!result ? (
          <div className="space-y-3">
            <Button variant="outline" onClick={downloadTemplate}
                    data-testid={`${testIdPrefix}-template-btn`}
                    className="w-full rounded-full font-bold gap-2 min-h-[48px]">
              <Download className="w-4 h-4" /> Download CSV template
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Tip: open the template in Excel or Google Sheets, fill it in, and save as CSV.
            </p>
            <div className="rounded-2xl border-2 border-dashed p-6 text-center space-y-2"
                 onDragOver={(e) => e.preventDefault()}
                 onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}>
              <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
              <p className="text-sm font-bold">Drop your CSV here</p>
              <p className="text-xs text-muted-foreground">or</p>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
                     data-testid={`${testIdPrefix}-file-input`}
                     onChange={(e) => handleFile(e.target.files?.[0])} />
              <Button onClick={() => fileRef.current?.click()} disabled={busy}
                      data-testid={`${testIdPrefix}-pick-btn`}
                      className="rounded-full font-bold" style={{ background: "hsl(var(--client))" }}>
                {busy ? "Uploading…" : "Choose file"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid={`${testIdPrefix}-result`}>
            <div className="rounded-2xl bg-emerald-500/10 border-2 border-emerald-500/30 p-4 flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <div>
                <div className="font-bold text-base">{result.created} row{result.created === 1 ? "" : "s"} imported</div>
                <div className="text-xs text-muted-foreground">{result.skipped || 0} skipped</div>
              </div>
            </div>
            {Array.isArray(result.errors) && result.errors.length > 0 && (
              <div className="rounded-2xl bg-amber-500/10 border-2 border-amber-500/30 p-3 space-y-2 max-h-56 overflow-auto"
                   data-testid={`${testIdPrefix}-errors`}>
                <div className="flex items-center gap-2 font-bold text-sm">
                  <AlertTriangle className="w-4 h-4 text-amber-600" /> Errors
                </div>
                <ul className="text-xs space-y-1">
                  {result.errors.map((er, i) => (
                    <li key={i}><span className="font-mono font-bold">Row {er.row}</span> — {er.reason}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={reset} className="flex-1 rounded-full font-bold" data-testid={`${testIdPrefix}-import-more-btn`}>Import more</Button>
              <Button onClick={() => handleClose(false)} className="flex-1 rounded-full font-bold" style={{ background: "hsl(var(--client))" }} data-testid={`${testIdPrefix}-close-btn`}>Done</Button>
            </div>
          </div>
        )}

        {!result && (
          <DialogFooter><span className="text-xs text-muted-foreground mx-auto">Existing rows are kept — this only adds new ones.</span></DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
